import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  centralMinute,
  clockParam,
  parseClockParam,
  shiftDate,
  todayCentral,
} from '../utils/cameraRelay';
import CamerasStyles, { C, Icon } from '../components/cameras/CamerasStyles';
import StatusPill from '../components/cameras/StatusPill';
import SecurityPill from '../components/cameras/SecurityPill';
import AlertsView from '../components/cameras/AlertsView';
import { DoorAlarmBanners, DoorsLine } from '../components/cameras/DoorAlarm';
import { useNightWatchStatus } from '../features/security/useNightWatch';
import { desktopUi } from '../features/desktopUi';
import LiveView, { TILE_SIZES } from '../components/cameras/LiveView';
import PlaybackView from '../components/cameras/PlaybackView';
import AllCamerasView from '../components/cameras/AllCamerasView';
import { useRelay, useRetention, useStationActivity } from '../components/cameras/useCameraData';

// Desktop cameras page: Live (grid → focus view) and Playback (one camera with
// the day bar, filmstrip and the station's JobQ jobs; or every camera at one
// moment). The relay is the Pi NVR behind cameras.kancraftmfg.com; JobQ data
// comes from /stations/activity and /stations/{station}/timeline/{date}.
// URL: /cameras?mode=playback&view=one&cam=laser-1&date=2026-10-05&t=10:45:00
// Night watch (2026-10-15): the pill beside the health pill, ?mode=alerts (tonight + the alerts), the
// tiles' Watched tags + last-alert caption, and a Night watch lane on Playback's day bar
// (features/security).
// Doors (2026-10-25): the doors line beside the pill (links to /doors), the hold / siren banners under the
// header, door alerts in the Alerts list and door chips on the tiles (features/security/doors.js).

const routerLink = (href, children, style) => <Link to={href} style={style}>{children}</Link>;

const SIZE_KEY = 'jqc-tile-size';

function readSize() {
  try {
    const v = localStorage.getItem(SIZE_KEY);
    return TILE_SIZES.some((s) => s.key === v) ? v : 'l';
  } catch (e) {
    return 'l';
  }
}

export default function CamerasPage() {
  const [params, setParams] = useSearchParams();
  const { cameras, health, healthError } = useRelay();
  const retention = useRetention(cameras && cameras[0]);

  const modeParam = params.get('mode');
  const mode = modeParam === 'playback' || modeParam === 'alerts' ? modeParam : 'live';
  const { status: security, error: securityError, reload: reloadSecurity } = useNightWatchStatus(true);
  const view = params.get('view') === 'all' ? 'all' : 'one';
  const today = todayCentral();
  const dateParam = params.get('date');
  const date = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && dateParam <= today ? dateParam : today;
  const camParam = params.get('cam');

  const { activity } = useStationActivity(mode === 'live');

  const [filter, setFilter] = useState('All');
  const [size, setSizeState] = useState(readSize);
  const [aspects, setAspects] = useState({});
  const [toast, setToast] = useState('');
  const playheadRef = useRef(null);
  const nonceRef = useRef(0);

  // The first jump comes from the link's t=, once.
  const [request, setRequest] = useState(() => {
    const m = parseClockParam(params.get('t'));
    return m == null ? null : { minute: m, play: false, nonce: 0 };
  });
  const [allMinute, setAllMinute] = useState(() => {
    const m = parseClockParam(params.get('t'));
    if (m != null) return m;
    return date === today ? Math.max(0, Math.floor(centralMinute()) - 5) : 600;
  });

  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setParams]);

  const setSize = (s) => {
    setSizeState(s);
    try { localStorage.setItem(SIZE_KEY, s); } catch (e) { /* private mode */ }
  };

  const onFrameSize = useCallback((key, w, h) => {
    if (!w || !h) return;
    const ratio = w / h;
    setAspects((prev) => (prev[key] && Math.abs(prev[key] - ratio) < 0.05 ? prev : { ...prev, [key]: ratio }));
  }, []);

  const onPlayhead = useCallback((m) => { playheadRef.current = m; }, []);

  const ask = (minute, play) => {
    nonceRef.current += 1;
    setRequest({ minute, play, nonce: nonceRef.current });
  };

  const openPlayback = useCallback((camera, day, minute) => {
    nonceRef.current += 1;
    setRequest({ minute, play: minute != null, nonce: nonceRef.current });
    update({ mode: 'playback', view: 'one', cam: camera.key, date: day === todayCentral() ? null : day, t: null });
  }, [update]);

  const playbackCam = useMemo(() => {
    if (!cameras || !cameras.length) return null;
    return cameras.find((c) => c.key === camParam) || cameras[0];
  }, [cameras, camParam]);
  const focusKey = mode === 'live' && cameras && cameras.some((c) => c.key === camParam) ? camParam : null;

  const copyLink = async (minute) => {
    const cam = playbackCam ? playbackCam.key : '';
    const url = `${window.location.origin}/cameras?mode=playback&view=one&cam=${cam}&date=${date}&t=${clockParam(minute)}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast('Link copied');
    } catch (e) {
      window.prompt('Copy this link', url);
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(id);
  }, [toast]);

  const setDate = (d) => {
    const clamped = d > today ? today : d;
    if (view === 'one') ask(null, false);
    update({ date: clamped === today ? null : clamped, t: null });
  };

  const switchView = (v) => {
    if (v === 'all' && playheadRef.current != null) setAllMinute(Math.floor(playheadRef.current));
    if (v === 'one') ask(allMinute, false);
    update({ view: v === 'all' ? 'all' : null });
  };

  if (cameras === null) {
    return (
      <div className="jqc-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CamerasStyles />
        <span style={{ color: C.muted }}>Loading cameras…</span>
      </div>
    );
  }

  const oldest = retention?.oldest || null;

  return (
    <div className="jqc-root">
      <CamerasStyles />
      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px', padding: '12px 24px', background: C.bar, borderBottom: '1px solid #22272d' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="camera" size={22} />
          <h1 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>Cameras</h1>
        </div>
        <div className="jqc-seg" role="group" aria-label="Mode">
          <button type="button" aria-pressed={mode === 'live'} onClick={() => update({ mode: null, view: null, cam: null, t: null, date: null })}>Live</button>
          <button type="button" aria-pressed={mode === 'playback'} onClick={() => { ask(null, false); update({ mode: 'playback', t: null }); }}>Playback</button>
          <button type="button" aria-pressed={mode === 'alerts'} onClick={() => update({ mode: 'alerts', view: null, cam: null, t: null, date: null })}>
            Alerts
            {(security?.recent_alerts || []).some((a) => a.outcome === 'texted' || a.outcome === 'unsure') && (
              <span className="jqc-dot" aria-label="alerts in the last 12 hours" style={{ background: C.bad, marginLeft: 6 }} />
            )}
          </button>
        </div>
        <span style={{ flex: 1 }} />
        <StatusPill cameras={cameras} health={health} healthError={healthError} retention={retention} />
        <SecurityPill status={security} error={securityError} cameras={cameras} ui={desktopUi} onChanged={reloadSecurity} />
        <DoorsLine doors={security?.doors} renderLink={routerLink} />
      </header>
      <DoorAlarmBanners doors={security?.doors} ui={desktopUi} onChanged={reloadSecurity} renderLink={routerLink} />

      {mode === 'alerts' && (
        <AlertsView
          cameras={cameras}
          status={security}
          ui={desktopUi}
          onWatchClip={(camera, day, minute) => camera && openPlayback(camera, day, minute)}
          onLive={(key) => update({ mode: null, view: null, cam: key, t: null, date: null })}
          onChanged={reloadSecurity}
        />
      )}
      {mode === 'live' ? (
        <LiveView
          security={security}
          cameras={cameras}
          aspects={aspects}
          onFrameSize={onFrameSize}
          activity={activity}
          health={health}
          filter={filter}
          onFilter={setFilter}
          focusKey={focusKey}
          onFocus={(key) => update({ cam: key })}
          size={size}
          onSize={setSize}
          onOpenPlayback={openPlayback}
        />
      ) : mode === 'playback' ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 12px', padding: '14px 24px 0' }}>
            <div className="jqc-seg" role="group" aria-label="View">
              <button type="button" aria-pressed={view === 'one'} onClick={() => switchView('one')}>One camera</button>
              <button type="button" aria-pressed={view === 'all'} onClick={() => switchView('all')}>All cameras</button>
            </div>
            {view === 'one' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cameras.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className="jqc-chip"
                    aria-pressed={playbackCam?.key === c.key}
                    onClick={() => { if (c.key !== playbackCam?.key) { ask(playheadRef.current, false); update({ cam: c.key }); } }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            <span style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" className="jqc-btn jqc-icon" aria-label="Previous day" onClick={() => setDate(shiftDate(date, -1))} disabled={oldest != null && date <= oldest}>
                <Icon name="left" />
              </button>
              <label className="jqc-btn" style={{ cursor: 'pointer' }}>
                <Icon name="calendar" />
                <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Date</span>
                <input
                  type="date"
                  value={date}
                  min={oldest || undefined}
                  max={today}
                  onChange={(e) => e.target.value && setDate(e.target.value)}
                  style={{ background: 'transparent', border: 0, color: C.text, font: 'inherit', fontSize: 13 }}
                />
              </label>
              <button type="button" className="jqc-btn jqc-icon" aria-label="Next day" onClick={() => setDate(shiftDate(date, 1))} disabled={date >= today}>
                <Icon name="right" />
              </button>
              <button type="button" className="jqc-btn" onClick={() => setDate(today)} disabled={date === today}>Today</button>
            </div>
          </div>
          {view === 'one' && playbackCam && (
            <PlaybackView
              key={`${playbackCam.key}|${date}`}
              camera={playbackCam}
              aspect={aspects[playbackCam.key]}
              date={date}
              request={request}
              onPlayhead={onPlayhead}
              onCopyLink={copyLink}
            />
          )}
          {view === 'all' && (
            <AllCamerasView
              cameras={cameras}
              aspects={aspects}
              date={date}
              minute={allMinute}
              onMinute={setAllMinute}
              onOpenCamera={(key, m) => { ask(m, true); update({ view: null, cam: key }); }}
            />
          )}
          {view === 'one' && !playbackCam && (
            <div style={{ padding: 48, textAlign: 'center', color: C.faint }}>No cameras reported by the relay.</div>
          )}
        </>
      ) : null}

      {toast && (
        <div role="status" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '10px 16px', borderRadius: 10, background: C.text, color: C.bg, fontWeight: 600, zIndex: 60 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
